import { css, html, LitElement } from 'lit'
import { property, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { PaginationItem } from './types'

@safeCustomElement('mdc-pagination')
export class MdcPagination extends LitElement {
  @property({ type: String, attribute: 'previous-href' }) previousHref = ''
  @property({ type: String, attribute: 'next-href' }) nextHref = ''
  @property({ type: String, reflect: true, attribute: 'current-page' }) currentPage = ''
  @property({ type: Array }) items: PaginationItem[] = []
  @state() private currentHash = ''

  connectedCallback() {
    super.connectedCallback()
    this.updateCurrentHash()
    window.addEventListener('hashchange', this.handleHashChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('hashchange', this.handleHashChange)
  }

  private handleHashChange = () => {
    this.updateCurrentHash()
    this.requestUpdate()
  }

  private updateCurrentHash() {
    this.currentHash = window.location.hash || '#page1'
  }

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-pagination,
		.mdc-pagination *,
		.mdc-pagination *::before,
		.mdc-pagination *::after {
			box-sizing: border-box;
		}

		.mdc-pagination__list {
			list-style: none;
			padding: 0;
			margin: 0;
			display: flex;
			align-items: center;
			gap: var(--spacing-050, 0.5rem);
		}

		.mdc-pagination__item {
			list-style: none;
			padding: 0;
			margin: 0;
		}

		.mdc-pagination__items {
			display: flex;
			align-items: center;
			gap: var(--spacing-050, 0.5rem);
		}

		.mdc-pagination-item {
			box-sizing: border-box;
			text-decoration: none;
			padding: 0;
			margin: 0;
			border: none;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 2.5rem;
			height: 2.5rem;
			font-size: var(--text-150, 1.5rem);
			color: var(--color-functional-gray-200);
			font-weight: 600;
			line-height: 1;
			letter-spacing: -0.015rem;
			border-radius: var(--radius-md, 0.5rem);
			padding: var(--spacing-025, 0.25rem) 0;
			transition: color 0.2s ease-in-out;
		}

		a.mdc-pagination-item:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		a.mdc-pagination-item:hover,
		a.mdc-pagination-item:focus-visible {
			color: var(--color-functional-gray-900);
		}

		.mdc-pagination-item--active {
			--_mdc-pagination-item--active-underline-gap: 0.3rem;
			--_mdc-pagination-item--active-underline-height: 0.125rem;

			flex-direction: column;
			color: var(--color-functional-gray-900);
			margin-top: calc(
				var(--_mdc-pagination-item--active-underline-height) +
					var(--_mdc-pagination-item--active-underline-gap)
			);
			gap: var(--_mdc-pagination-item--active-underline-gap);
		}

		.mdc-pagination-item--active::after {
			content: '';
			display: block;
			width: 1rem;
			height: var(--_mdc-pagination-item--active-underline-height);
			margin-right: auto;
			margin-left: auto;
			background-color: var(--color-functional-gray-black);
		}
	`

  render() {
    return html`
			<nav class="mdc-pagination" aria-label="pagination">
				<ul class="mdc-pagination__list">
					<li class="mdc-pagination__item">
						<mdc-icon-button
							type="tertiary"
							iconSize="default"
							size="lg"
							icon="chevron-left"
							label="Previous"
							href=${this.previousHref || ''}
							?disabled=${!this.previousHref}
						></mdc-icon-button>
					</li>
					<li class="mdc-pagination__item mdc-pagination__items">
						${this.items.map((item) => {
              // Determine active state: prioritize currentPage prop, fallback to href match
              let isActive = false
              if (this.currentPage) {
                isActive = item.text === this.currentPage
              } else if (item.href) {
                isActive = item.href === this.currentHash
              }
              const className = `mdc-pagination-item${isActive ? ' mdc-pagination-item--active' : ''}`
              return item.href
                ? html`<a class=${className} href=${item.href}>${item.text}</a>`
                : html`<span class=${className}>${item.text}</span>`
            })}
					</li>
					<li class="mdc-pagination__item">
						<mdc-icon-button
							type="tertiary"
							iconSize="default"
							size="lg"
							icon="chevron-right"
							label="Next"
							href=${this.nextHref || ''}
							?disabled=${!this.nextHref}
						></mdc-icon-button>
					</li>
				</ul>
			</nav>
		`
  }
}
